import { createTheme, ThemeProvider, Typography } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'
import { useUser } from 'api/users/users.api'
import { getMRTUserLocalization } from 'localization/localization.helper'
import { MaterialReactTable, MRT_ColumnDef, MRT_Row, MRT_RowData, MRT_SortingState } from 'material-react-table'
import React, { ReactElement, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import theme from 'styles/theme/theme'
import { colors } from 'styles/variables'
import sentry from 'utils/sentry/sentry'
import styles from './CustomTable.module.less'

// Legacy types for backward compatibility
export type LegacyColumn<D extends Record<string, unknown> = Record<string, unknown>> = {
  Header?: string | ReactNode
  header?: string | ReactNode
  accessor: string
  Cell?: (props: { value: any; row: { original: D; id: string } }) => ReactNode
  disableSortBy?: boolean
  sortType?: (rowA: any, rowB: any, id: string, desc: boolean | undefined) => number
}

export type LegacyTableOptions<D extends Record<string, unknown> = Record<string, unknown>> = {
  columns?: LegacyColumn<D>[]
  data?: D[]
  initialState?: {
    pageSize?: number
    sortBy?: { id: string; desc: boolean }[]
    hiddenColumns?: string[]
    globalFilter?: string
  }
  autoResetGlobalFilter?: boolean
}

// Convert legacy column format to MRT column format
function convertColumns<D extends MRT_RowData>(legacyColumns: LegacyColumn<D>[]): MRT_ColumnDef<D>[] {
  const accessorSet = new Set<string>()
  const indicesToRemove = new Set<number>()

  legacyColumns.forEach((col, index) => {
    if (accessorSet.has(col.accessor)) {
      indicesToRemove.add(index)
      sentry.captureScopedMessage({
        scope: 'custom_table',
        issueType: 'duplicate_columns',
        message: 'React table has duplicate columns.',
        tags: {
          duplicate_accessor: col.accessor,
        },
        extra: {
          duplicateAccessor: col.accessor,
          duplicateIndex: index,
          columnCount: legacyColumns.length,
        },
        fingerprint: ['custom_table', 'duplicate_columns', col.accessor],
      })
    } else {
      accessorSet.add(col.accessor)
    }
  })

  return legacyColumns
    .filter((_, index) => !indicesToRemove.has(index))
    .map((col) => {
      const headerValue = col.Header ?? col.header ?? ''
      const mrtColumn: MRT_ColumnDef<D> = {
        accessorKey: col.accessor as keyof D & string,
        header: typeof headerValue === 'string' ? headerValue : '',
        Header: typeof headerValue !== 'string' ? () => headerValue : undefined,
        size: 0,
        enableSorting: !col.disableSortBy,
      }

      if (col.Cell) {
        const cellRenderer = col.Cell
        mrtColumn.Cell = ({ cell, row }) => {
          const legacyProps = {
            value: cell.getValue(),
            row: { original: row.original, id: row.id },
          }
          return cellRenderer(legacyProps as any)
        }
      }

      if (col.sortType) {
        const sortFn = col.sortType
        mrtColumn.sortingFn = (rowA, rowB, columnId) => {
          return sortFn(rowA, rowB, columnId, false)
        }
      }

      return mrtColumn
    })
}

type DefaultTableProps<D extends Record<string, unknown> = Record<string, unknown>> = {
  columns: LegacyColumn<D>[]
  data: D[]
  title?: string
  tableOptions?: Partial<LegacyTableOptions<D>>
  emptyTableText?: string
  isDatasetTable?: boolean
  rowClass?: string | ((row: MRT_Row<D>) => string | undefined)
  everyOtherColumnGrey?: boolean
  initialPageSize?: number
  initialSortBy?: { id: string; desc: boolean }[]
  autoResetAll?: boolean
  enableGlobalFilter?: boolean
  enableFullScreenToggle?: boolean
  enableDensityToggle?: boolean
  enableHiding?: boolean
}

export default function DefaultTable<D extends Record<string, unknown> = Record<string, unknown>>({
  columns,
  data,
  title,
  isDatasetTable,
  everyOtherColumnGrey,
  emptyTableText,
  tableOptions,
  rowClass,
  initialPageSize,
  initialSortBy,
  autoResetAll,
  enableGlobalFilter = false,
  enableFullScreenToggle = false,
  enableDensityToggle = false,
  enableHiding = false,
}: DefaultTableProps<D>): ReactElement {
  const { t } = useTranslation()
  const { data: user } = useUser()
  const userLocalization = getMRTUserLocalization(user?.settings.language)

  const mrtColumns = useMemo(() => convertColumns(columns as LegacyColumn<D>[]), [columns])

  const hiddenColumns = useMemo(() => {
    const hidden: Record<string, boolean> = {}
    tableOptions?.initialState?.hiddenColumns?.forEach((col) => {
      hidden[col] = false
    })
    return hidden
  }, [tableOptions?.initialState?.hiddenColumns])

  const columnOrder = useMemo(() => {
    return mrtColumns.map((col) => col.accessorKey as string)
  }, [mrtColumns])

  const initialSorting: MRT_SortingState = useMemo(() => {
    const sortBy = initialSortBy ?? tableOptions?.initialState?.sortBy ?? []
    return sortBy
      .filter((s) => s.id !== '')
      .map((s) => ({
        id: s.id,
        desc: s.desc,
      }))
  }, [initialSortBy, tableOptions?.initialState?.sortBy])

  const pageSize = initialPageSize ?? tableOptions?.initialState?.pageSize ?? (isDatasetTable ? 25 : 10)

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: theme.palette.primary,
          secondary: theme.palette.secondary,
          info: theme.palette.info,
        },
      }),
    []
  )

  return (
    <div className={isDatasetTable ? styles.CustomTable_Table__overflow : undefined}>
      {title ? (
        <Typography variant="h1" className={styles.CustomTable_Title}>
          {title}
        </Typography>
      ) : null}
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={mrtColumns}
            data={data}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableGlobalFilter={enableGlobalFilter}
            enableStickyHeader
            enableFullScreenToggle={enableFullScreenToggle}
            enableDensityToggle={enableDensityToggle}
            enableColumnDragging={false}
            enableHiding={enableHiding}
            layoutMode="grid"
            positionGlobalFilter="left"
            localization={userLocalization}
            globalFilterFn="includesString"
            autoResetAll={autoResetAll}
            state={{
              columnOrder,
            }}
            initialState={{
              showGlobalFilter: true,
              sorting: initialSorting,
              columnVisibility: hiddenColumns,
              pagination: {
                pageIndex: 0,
                pageSize,
              },
            }}
            muiSearchTextFieldProps={{
              placeholder: t(`Search`),
              variant: 'outlined',
            }}
            muiTablePaperProps={{
              elevation: 0,
            }}
            muiTopToolbarProps={() => ({
              sx: {
                display:
                  !enableDensityToggle && !enableFullScreenToggle && !enableGlobalFilter && !enableHiding
                    ? 'none'
                    : undefined,
              },
            })}
            muiTableHeadCellProps={({ column }) => ({
              sx: {
                backgroundColor: everyOtherColumnGrey && column.getIndex() % 2 !== 0 ? colors.grey2 : undefined,
                lineHeight: '1rem',
              },
            })}
            muiTableBodyCellProps={({ column }) => ({
              sx: {
                backgroundColor: everyOtherColumnGrey && column.getIndex() % 2 !== 0 ? colors.grey2 : undefined,
              },
            })}
            muiTableBodyRowProps={({ row }) => ({
              className: typeof rowClass === 'string' ? rowClass : rowClass?.(row as MRT_Row<D>),
            })}
            muiBottomToolbarProps={{
              sx: {
                display: data.length === 0 ? 'none' : undefined,
              },
            }}
            renderEmptyRowsFallback={() => (
              <div style={{ textAlign: 'center', padding: '1rem' }}>{emptyTableText ?? t(`No rows found`)}</div>
            )}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  )
}
